import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Boxes } from "lucide-react";
import type { InventoryExceptionList, InventoryExceptionsDto } from "@hisense/shared";

/**
 * Việc kho tồn đọng trên Bảng điều khiển (18/09/2026: Kho & LOT là nguồn tồn duy nhất).
 *
 * Phần mềm không ngăn được người mang hàng ra khỏi kho mà không ghi — nó làm cho việc bỏ qua lộ ra nhanh. Bốn loại:
 * phiếu giao đã rời kho chưa ghi nguồn hàng; hoá đơn bán mã đang có tồn chưa có phiếu giao; đơn mua báo đã về chưa nhập
 * kho; giao thẳng trong lúc kho có hàng mà người Duyệt chưa xem lại (0088). Chưa ghi sổ tồn đầu kỳ nào thì chỉ đường sang
 * nhập tồn đầu kỳ, không nói "không có việc".
 */
export function InventoryExceptions({ data }: { data: InventoryExceptionsDto | null }) {
  const { t } = useTranslation();
  if (!data) {
    return (
      <p className="mt-3 flex flex-wrap items-center gap-2 rounded-sm border border-line bg-surface px-3 py-2 text-xs text-ink-soft">
        <Boxes className="size-4 text-ink-faint" aria-hidden="true" />
        {t("dashboard.inventory.notStarted")}
        <Link to="/stock-moves/counts" className="text-brand hover:underline">{t("dashboard.inventory.openCounts")}</Link>
      </p>
    );
  }
  const rows: [string, InventoryExceptionList, (id: string) => string][] = [
    ["deliveredNotIssued", data.deliveredNotIssued, (id) => `/delivery-notes/${id}`],
    ["invoicesFromStock", data.invoicesFromStock, (id) => `/invoices/${id}`],
    ["receivedNotStocked", data.receivedNotStocked, (id) => `/purchase-orders/${id}`],
    ["directWithStock", data.directWithStock, (id) => `/delivery-notes/${id}`],
  ];
  const open = rows.filter(([, list]) => list.count > 0);
  if (open.length === 0) {
    return (
      <p className="mt-3 flex items-center gap-2 text-xs text-ok">
        <Boxes className="size-4" aria-hidden="true" />
        {t("dashboard.inventory.clear", { since: data.since })}
      </p>
    );
  }
  return (
    <section className="mt-3 rounded-sm border border-warn/40 bg-warn-tint px-3 py-2" aria-label={t("dashboard.inventory.title")}>
      <p className="flex items-center gap-2 text-xs font-semibold text-ink">
        <Boxes className="size-4 text-warn" aria-hidden="true" />
        {t("dashboard.inventory.title")}
        <span className="font-normal text-ink-soft">{t("dashboard.inventory.since", { since: data.since })}</span>
      </p>
      <ul className="mt-1.5 space-y-1">
        {open.map(([key, list, href]) => (
          <li key={key} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-ink">
            <span className="font-mono tabular text-sm font-semibold text-warn">{list.count}</span>
            <span>{t(`dashboard.inventory.${key}`)}</span>
            <span className="flex flex-wrap gap-1.5">
              {list.items.map((it) => (
                <Link key={it.id} to={href(it.id)} className="font-mono tabular text-brand hover:underline">{it.no}</Link>
              ))}
              {list.count > list.items.length && <span className="text-ink-faint">…</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
